import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import Search from '@components/icons/Search.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { useAuthContext } from '@providers/AuthProvider';
import { useAPI } from '@providers/useAPI';
import { GetOrgMembersMemberResponseDto, OrganizationsService, PrismaUserDto, RepositoriesService } from 'app/API';
import { TRepository } from 'app/types/entities/TRepository';
import { debounce } from 'lodash-es';
import React, { useEffect } from 'react';
import { Content } from './AssignRepositoryModal/styles/Content';

type AssigneeLike = GetOrgMembersMemberResponseDto | PrismaUserDto;

type TProps = {
  repositoryId: string;
  repository: TRepository & { assignee?: AssigneeLike | null };
  onCancel: () => void;
  onSuccess: (assignee: AssigneeLike | null) => void;
};

export const AssignRepositoryModal = ({ repositoryId, repository, onCancel, onSuccess }: TProps) => {
  const { call } = useAPI();
  const [users, setUsers] = React.useState<GetOrgMembersMemberResponseDto[]>([]);
  const { organization } = useAuthContext();
  const [search, setSearch] = React.useState('');
  const [assignee, setAssignee] = React.useState<AssigneeLike | null>(repository.assignee ?? null);

  useEffect(() => {
    const debouncedSearch = debounce(() => {
      call(
        OrganizationsService.getOrgMembers({
          oid: organization?.id ?? '',
          orderBy: 'name',
          orderType: 'asc',
          pageNo: '1',
          pageSize: '1000',
          search,
          teamsSlugFilter: '',
        }),
      ).then((result) => {
        setUsers(result.data);
      });
    }, 500);

    debouncedSearch();
    return () => debouncedSearch.cancel();
  }, [search]);

  const changeAssignee = (data: AssigneeLike | null) => {
    if (!data) {
      setAssignee(null);
      return;
    }
    setAssignee(data);
  };

  const renderAssignee = () => {
    if (!assignee) {
      return;
    }
    return (
      <div className="assignTo">
        <div className="assignToText">
          <p>
            {assignee.name || ''} {assignee.lastname || ''}
          </p>
          {assignee.email ? <p>{assignee.email}</p> : ''}
        </div>
        <IconButtonDelete type="button" title="Clear assignee" onClick={() => changeAssignee(null)}>
          <TrashBin />
        </IconButtonDelete>
      </div>
    );
  };

  const handleSubmit = async () => {
    await call(RepositoriesService.reassignRepo({ repositoryId, requestBody: { assigneeId: assignee?.id ?? null } }));
    onSuccess(assignee);
  };

  return (
    <ModalWrapper width="487px">
      <ModalTitle>Edit assignee</ModalTitle>
      <Content>
        <p className="assignedTo">{repository.assigneeId ? 'Currently assigned to' : 'Currently not assigned'}</p>
        {renderAssignee()}
        <Input
          onChange={(e) => setSearch(e.target.value)}
          label="Search user to assign"
          name="assignUser"
          icon={Search}
          value={search}
        />
        <TableWrapper className="customTable">
          <Table>
            <Thead $white>
              <Tr>
                <Th>Name</Th>
                <Th>Email</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.map((data) => (
                <Tr key={data.id}>
                  <Td>
                    <p className="cursor-pointer" onClick={() => changeAssignee(data)}>
                      {data?.name}
                    </p>
                  </Td>
                  <Td>
                    <p className="cursor-pointer" onClick={() => changeAssignee(data)}>
                      {data?.email}
                    </p>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableWrapper>

        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button onClick={handleSubmit}>Save</Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
